import React, { useState, useEffect } from "react";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "assets/svgIcons/AccordionExpandMoreIcon";
import _ from "lodash";
import { useTranslation } from "react-i18next";
import {
    isUserAdmin,
    isUserSupport,
    isUserMaster
} from "../../../../constant";
import {
    Heading,
    WorkSection,
    ActionWrapper,
    LiabilityWaiver,
    WaiverUploadBtnWrapper
} from "pages/Fleet/Settings/SettingsStyled";
import { useHookstate } from "@hookstate/core";
import { SITEID_GLOBAL_STATE } from "globalStore/globalStore";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "@mui/material";
import "react-quill/dist/quill.snow.css";
import { COMMON_SERVICES } from "redux/reduxToolkitServices/_commonServices/commonServices";
import moment from "moment";
import ReactQuill from "react-quill";
import { waiverCss } from "../waiver";

export const LiabilityWaiverAccordion = ({ waiverPublishedDate,
    setWaiverPublishedDate,
    logoInputRef,
    backgroundInputRef
}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const { siteId } = useHookstate(SITEID_GLOBAL_STATE);


    const { siteSettingsData, getUserRole, getWaiverTemplatesData } = useSelector((state) => state?.commonReducers) || [];

    const [logoImage, setLogoImage] = useState(null);
    const [logoImageDisplay, setLogoImageDisplay] = useState(null);
    const [backgroundImage, setBackgroundImage] = useState(null);
    const [backgroundImageDisplay, setBackgroundImageDisplay] = useState(null);
    const [waiverEditorHtml, setWaiverEditorHtml] = useState('');
    const [isSaveWaiverButtonDisabled, setIsSaveWaiverButtonDisabled] = useState(true);

    useEffect(() => {
        if (Object.keys(getWaiverTemplatesData).length > 0) {
            setLogoImageDisplay(getWaiverTemplatesData.LogoImage ? `data:image/png;base64,${getWaiverTemplatesData.LogoImage}` : null);
            setBackgroundImageDisplay(getWaiverTemplatesData.BackgroundImage ? `data:image/png;base64,${getWaiverTemplatesData.BackgroundImage}` : null);
            setWaiverEditorHtml(getWaiverTemplatesData.HtmlContent || '');

            setWaiverPublishedDate(getWaiverTemplatesData.PublishedDate ? 'Last Published ' + moment.utc(getWaiverTemplatesData.PublishedDate).utcOffset(-siteSettingsData?.TimeZoneOffset).format("DD-MMM-YYYY HH:mm:ss") : "Not Published Yet");
        }
    }, [getWaiverTemplatesData]);

    const modules = {
        toolbar: [
            [{ header: "1" }, { header: "2" }, { font: [] }],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }],
            ["bold", "italic", "underline", "strike"],
            ["link", "image"],
            [{ color: [] }, { background: [] }],
            ["blockquote", "code-block"],
        ],
    };

    const handleCancelWaiverTemplate = () => {
        // Resetting state to previously saved data
        setLogoImage(null);
        setBackgroundImage(null);
        setLogoImageDisplay(getWaiverTemplatesData.LogoImage ? `data:image/png;base64,${getWaiverTemplatesData.LogoImage}` : null);
        setBackgroundImageDisplay(getWaiverTemplatesData.BackgroundImage ? `data:image/png;base64,${getWaiverTemplatesData.BackgroundImage}` : null);
        setWaiverEditorHtml(getWaiverTemplatesData.HtmlContent || '');
        setIsSaveWaiverButtonDisabled(true);

        if (logoInputRef.current) {
            logoInputRef.current.value = '';
        }

        if (backgroundInputRef.current) {
            backgroundInputRef.current.value = '';
        }
    }

    const handleLogoImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoImage(file);

            const reader = new FileReader();
            reader.onloadend = () => {
                let base64String = reader.result.split(',')[1];
                base64String = cleanBase64String(base64String);
                setLogoImage(base64String);

                setLogoImageDisplay(reader.result);
            };
            reader.readAsDataURL(file);

            setIsSaveWaiverButtonDisabled(false);
        }
    }

    const clearLogoImage = (e) => {
        setLogoImage(null);
        setLogoImageDisplay(null);

        if (logoInputRef.current) {
            logoInputRef.current.value = '';
        }

        setIsSaveWaiverButtonDisabled(false);
    }

    const handleBackgroundImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                let base64String = reader.result.split(',')[1];
                base64String = cleanBase64String(base64String);
                setBackgroundImage(base64String);

                setBackgroundImageDisplay(reader.result);
            };
            reader.readAsDataURL(file);

            setIsSaveWaiverButtonDisabled(false);
        }
    }

    const clearBackgroundImage = (e) => {
        setBackgroundImage(null);
        setBackgroundImageDisplay(null);

        if (backgroundInputRef.current) {
            backgroundInputRef.current.value = '';
        }

        setIsSaveWaiverButtonDisabled(false);
    }

    const handleWaiverEditorHtmlChange = (value) => {

        // replace special characters
        value = value || '';
        value = value.replaceAll('“', '&ldquo;'); // fancy left double quote
        value = value.replaceAll('”', '&rdquo;'); // fancy right double quote
        value = value.replaceAll('‘', '&lsquo;'); // fancy left single quote
        value = value.replaceAll('’', '&rsquo;'); // fancy right single quote

        setWaiverEditorHtml(value);
    }

    const handlePublishWaiverTemplate = () => {
        var request = {
            siteId: parseInt(siteId.get())
        };

        dispatch(COMMON_SERVICES.publishWaiverTemplateService(request)).then(() => {
            dispatch(COMMON_SERVICES.getWaiverTemplateService(parseInt(siteId.get()))); // reload waiver to get published date
        });
    }

    const handleUnpublishWaiverTemplate = () => {
        var request = {
            siteId: parseInt(siteId.get())
        };

        dispatch(COMMON_SERVICES.unpublishWaiverTemplateService(request)).then(() => {
            dispatch(COMMON_SERVICES.getWaiverTemplateService(parseInt(siteId.get()))); // reload waiver to get published date (which should be null now after unpublishing)
        });
    }

    const handleSaveWaiverTemplate = () => {
        var request = {
            logoImage: logoImage,
            backgroundImage: backgroundImage,
            waiverTemplate: waiverEditorHtml,
            siteId: parseInt(siteId.get()),
            htmlFile: generateHtmlFile()
        };

        dispatch(
            COMMON_SERVICES.saveWaiverTemplateService(request)
        );

        setIsSaveWaiverButtonDisabled(true);
    }

    const cleanBase64String = (str) => {
        return str.replace(/\s/g, '').replace(/(\r\n|\n|\r)/gm, '');
    }

    const generateHtmlFile = () => {
        var bg = backgroundImageDisplay ? `url('${backgroundImageDisplay}')` : 'none';
        var contentTopPadding = logoImageDisplay ? '110px' : '10px';
        var logo = logoImageDisplay ? `<img class='logo' src='${logoImageDisplay}' />` : '';

        return `<html>
                <head>
                  <style>
                    body {
                        margin: 0;
                        padding: 0;
                        background-color: white;
                        font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Oxygen", "Ubuntu", "Cantarell", "Fira Sans", "Droid Sans", "Helvetica Neue", sans-serif;
                    }
    
                    .background {
                        padding: 0;
                        width: 1280px;
                        height: 800px;
                        margin: 0 auto;
                        background-image: ${bg};
                        background-size: cover;
                        background-repeat: no-repeat;
                        background-attachment: fixed;
                        background-position: center;
                        overflow-y: auto;
                    }
    
                    .content {
                        padding-left: 30px !important;
                        padding-right: 30px !important;
                        padding-top: ${contentTopPadding} !important;
                        padding-bottom: 120px !important;
                        white-space: normal !important;
                    }
    
                    .logo {
                        height: 100px;
                        position: absolute;
                        top: 10px;
                        left: 50%;
                        transform: translateX(-50%);
                        max-width: 100%;
                        z-index: 10
                    }
    
                    ${waiverCss}
                  </style>
                </head>
                <body>
                  <div class="background" >
                    <div class="ql-editor content" >
                        ${logo}
                        ${waiverEditorHtml}
                    </div>
                  </div>
                </body>
              </html>
            `;
    }

    return (
        <>
            <Accordion>
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls="panel1a-content"
                    id="panel1a-header"
                >
                    <Heading>
                        <h3>{t("Liability_Waiver")}</h3>
                    </Heading>
                </AccordionSummary>
                <AccordionDetails>
                    <WorkSection>
                        <LiabilityWaiver>
                            <div className="flex-parent">
                                <div className="child">
                                    <p className="label">Logo</p>
                                    <WaiverUploadBtnWrapper>
                                        <input type="file" className="waiver-input" ref={logoInputRef} onChange={handleLogoImageChange} disabled={!(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))} />
                                    </WaiverUploadBtnWrapper>
                                    <ActionWrapper>
                                        <Button
                                            style={{ marginTop: "10px" }}
                                            disabled={!logoImageDisplay || !(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))}
                                            onClick={() => clearLogoImage()}
                                            className={!logoImageDisplay || !(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role)) ? "disabled" : "active"}
                                        >
                                            {t("Remove_Logo")}
                                        </Button>
                                    </ActionWrapper>
                                </div>

                                <div className="child">
                                    <p className="label">Background Image</p>
                                    <WaiverUploadBtnWrapper>
                                        <input type="file" className="waiver-input" ref={backgroundInputRef} onChange={handleBackgroundImageChange} disabled={!(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))} />
                                    </WaiverUploadBtnWrapper>
                                    <ActionWrapper>
                                        <Button
                                            style={{ marginTop: "10px" }}
                                            disabled={!backgroundImageDisplay || !(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))}
                                            onClick={() => clearBackgroundImage()}
                                            className={!backgroundImageDisplay || !(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role)) ? "disabled" : "active"}
                                        >
                                            {t("Remove_Background")}
                                        </Button>
                                    </ActionWrapper>
                                </div>
                                <div className="child-two">
                                    <ReactQuill
                                        value={waiverEditorHtml}
                                        onChange={handleWaiverEditorHtmlChange}
                                        onKeyUp={() => isSaveWaiverButtonDisabled ? setIsSaveWaiverButtonDisabled(false) : () => { }}
                                        theme="snow"
                                        readOnly={!(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))}
                                        modules={modules}
                                        className="quill-editor"
                                    />
                                </div>
                            </div>
                            <div className="preview">
                                <h3>Preview:</h3>
                                <div style={{
                                    border: "1px solid #ccc",
                                    position: "relative",
                                    padding: "0",
                                    width: "1280px",
                                    height: "800px",
                                    margin: "0 auto",
                                    backgroundImage: backgroundImageDisplay ? `url(${backgroundImageDisplay})` : 'none',
                                    backgroundSize: "cover",
                                    backgroundRepeat: "no-repeat",
                                    backgroundAttachment: "local", /* this is set to fixed in rendered file for VDU use */
                                    backgroundPosition: "center",
                                    overflowY: "hidden"
                                }}
                                >
                                    <div
                                        className="ql-editor"
                                        style={{
                                            paddingLeft: "30px",
                                            paddingRight: "30px",
                                            paddingTop: logoImageDisplay ? "110px" : "10px",
                                            zIndex: 1
                                        }}>

                                        {logoImageDisplay &&
                                            <img
                                                src={logoImageDisplay}
                                                height={100}
                                                alt="Logo"
                                                style={{
                                                    position: "absolute",
                                                    top: "10px",
                                                    left: "50%",
                                                    transform: "translateX(-50%)",
                                                    maxWidth: "100%",
                                                    zIndex: 10
                                                }}
                                            />
                                        }

                                        <div
                                            dangerouslySetInnerHTML={{ __html: waiverEditorHtml }}
                                        />
                                    </div>

                                </div>
                            </div>

                            <ActionWrapper>
                                <Button
                                    disabled={isSaveWaiverButtonDisabled || !(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))}
                                    onClick={() => handleSaveWaiverTemplate()}
                                    className={isSaveWaiverButtonDisabled || !(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role)) ? "disabled" : "active"}
                                >
                                    {t("Save")}
                                </Button>
                                <Button onClick={handleCancelWaiverTemplate} disabled={!(isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))}>
                                    {t("Cancel")}
                                </Button>
                                <Button
                                    disabled={!isSaveWaiverButtonDisabled && (isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))}
                                    onClick={() => handlePublishWaiverTemplate()}
                                    style={{ marginLeft: "40px" }}
                                    className={!isSaveWaiverButtonDisabled && (isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role)) ? "disabled" : "active"}
                                >
                                    {t("Publish")}
                                </Button>
                                <Button
                                    disabled={!isSaveWaiverButtonDisabled && (isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role))}
                                    onClick={() => handleUnpublishWaiverTemplate()}
                                    className={!isSaveWaiverButtonDisabled && (isUserAdmin(getUserRole?.Role) || isUserSupport(getUserRole?.Role) || isUserMaster(getUserRole?.Role)) ? "disabled" : "active"}
                                >
                                    {t("Unpublish")}
                                </Button>
                                <div>{waiverPublishedDate}</div>
                            </ActionWrapper>
                        </LiabilityWaiver>
                    </WorkSection>
                </AccordionDetails>
            </Accordion>
        </>
    );
};